import {Integration} from "@/shared/timr";

interface PanelLayoutOptions {
	logoHtml: string;
	bodyHtml?: string;
	footerHtml?: string;
	topActionsHtml?: string;
}

/*
	 * This class is the base class for all banners.
	 * It provides the integration property and the updateBanner method.
	 * The updateBanner method should be called when the integration property is set.
	 *
	 * Naming convention for web components: <banner-${integrationType}>
	 *
	 * example: <banner-personio> / <banner-slack>
	 */
export abstract class PanelBase extends HTMLElement {
	// tslint:disable-next-line:variable-name
	protected _integration: Integration;

	set integration(integration: Integration) {
		this._integration = integration;
		this.updateBanner();
	}

	get integration(): Integration {
		return this._integration;
	}

	protected renderPanel({logoHtml, bodyHtml, footerHtml, topActionsHtml}: PanelLayoutOptions): string {
		const content = bodyHtml ? `<div class="panel-body p-20">${bodyHtml}</div>` : "";
		const footer = footerHtml ?
			`<div class="display-flex flex-valign-middle" style="margin-top: auto; flex-wrap: wrap;">
				${footerHtml}
			</div>` : "";
		const actions = `${topActionsHtml ?? ""}${this.renderRemoveActionButton()}`;

		return `
			<div class="panel panel-default p-20" style="display: flex; flex-direction: column;">
				<div class="display-flex justify-content-space-between flex-valign-middle">
					${logoHtml}
					<div class="display-flex flex-valign-middle" style="flex-wrap: nowrap; justify-content: flex-end;">
						${actions}
					</div>
				</div>
				${content}
				${footer}
			</div>
		`;
	}

	protected renderEditActionButton(id: string = "edit", messageCode: string = "integrations.edit"): string {
		return `<button type="button" id="${id}" class="btn btn-default mb-0 mt-0 mr-0">${messages[messageCode]}</button>`;
	}

	private renderRemoveActionButton(): string {
		return `<button type="button" data-delete-integration class="btn btn-outline-danger mb-0 mt-0 mr-0 ml-10">${messages["integrations.remove"]}</button>`;
	}

	protected abstract updateBanner(): void;

	public onIntegrationAdded() {
		// Might be overridden by subclasses
	}
}
